import m from 'mithril'
import type { ListDetail, ListVisibility } from '../../shared/types.ts'
import { api } from '../api.ts'
import { Empty, ErrorNote, Spinner, VisibilityPicker, formatDate, visibilityLabel } from '../ui.ts'

const EDIT_ICON = 'M4 20h4L18.5 9.5a2.1 2.1 0 0 0-4-4L4 16v4zM13.5 6.5l4 4'
const LINK_ICON =
  'M10 13.5a4 4 0 0 0 5.7.3l3-3a4 4 0 0 0-5.7-5.6l-1.2 1.2M14 10.5a4 4 0 0 0-5.7-.3l-3 3a4 4 0 0 0 5.7 5.6l1.2-1.2'
const CHECK_ICON = 'M5 12.5l4.5 4.5L19 7.5'

const icon = (d: string) =>
  m(
    'svg',
    {
      viewBox: '0 0 24 24',
      width: 20,
      height: 20,
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': 1.9,
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'aria-hidden': 'true',
    },
    m('path', { d }),
  )

export const ListView: m.ClosureComponent<{ id: string }> = (vnode) => {
  const id = vnode.attrs.id
  let data: ListDetail | null = null
  let error = ''
  let busy = false
  let editing = false
  let name = ''
  let description = ''
  let visibility: ListVisibility = 'public'
  let copied = false

  const load = async () => {
    try {
      data = await api<ListDetail>(`/api/lists/${id}`)
      name = data.list.name
      description = data.list.description
      visibility = data.list.visibility
      error = ''
    } catch (err) {
      error = (err as Error).message
    } finally {
      m.redraw()
    }
  }

  const save = async (event: Event) => {
    event.preventDefault()
    busy = true
    try {
      await api(`/api/lists/${id}`, { method: 'PATCH', body: { name, description, visibility } })
      editing = false
      await load()
    } catch (err) {
      error = (err as Error).message
    } finally {
      busy = false
      m.redraw()
    }
  }

  const removeEntry = async (restaurantRef: string) => {
    busy = true
    try {
      await api(`/api/lists/${id}/items/${restaurantRef}`, { method: 'DELETE' })
      await load()
    } finally {
      busy = false
      m.redraw()
    }
  }

  const destroy = async () => {
    if (!confirm('Delete this list?')) return
    busy = true
    try {
      await api(`/api/lists/${id}`, { method: 'DELETE' })
      m.route.set(data ? `/u/${data.list.owner.username}` : '/')
    } catch (err) {
      error = (err as Error).message
      busy = false
      m.redraw()
    }
  }

  return {
    oninit: load,
    view: () => {
      if (error && !data) return m(ErrorNote, { text: error })
      if (!data) return m(Spinner)

      const { list, entries, isMine } = data

      return m('section.stack', [
        editing
          ? m('form.card.stack', { onsubmit: save }, [
              m('label', [
                'Name',
                m('input', {
                  value: name,
                  oninput: (event: Event) => {
                    name = (event.target as HTMLInputElement).value
                  },
                }),
              ]),
              m('label', [
                'Description',
                m('textarea', {
                  rows: 2,
                  value: description,
                  oninput: (event: Event) => {
                    description = (event.target as HTMLTextAreaElement).value
                  },
                }),
              ]),
              m(VisibilityPicker, {
                value: visibility,
                onchange: (value: ListVisibility) => {
                  visibility = value
                },
              }),
              m('div.row', [
                m('button.button.primary', { type: 'submit', disabled: busy }, 'Save'),
                m(
                  'button.button',
                  {
                    type: 'button',
                    onclick: () => {
                      editing = false
                    },
                  },
                  'Cancel',
                ),
                m('button.button.danger', { type: 'button', onclick: destroy, disabled: busy }, 'Delete list'),
              ]),
            ])
          : m('header.card.profile-header', [
              m('div.list-title-row', [
                m('h1', [list.name, list.visibility !== 'public' ? m('span.badge', visibilityLabel(list.visibility)) : null]),
                isMine
                  ? m('div.icon-actions', [
                      list.visibility !== 'private'
                        ? m(
                            'button.icon-button',
                            {
                              type: 'button',
                              'aria-label': copied ? 'Link copied' : 'Copy link',
                              title: copied ? 'Link copied' : 'Copy link',
                              class: copied ? 'done' : '',
                              onclick: async () => {
                                await navigator.clipboard?.writeText(location.href)
                                copied = true
                                setTimeout(() => {
                                  copied = false
                                  m.redraw()
                                }, 2000)
                              },
                            },
                            icon(copied ? CHECK_ICON : LINK_ICON),
                          )
                        : null,
                      m(
                        'button.icon-button',
                        {
                          type: 'button',
                          'aria-label': 'Edit list',
                          title: 'Edit list',
                          onclick: () => {
                            editing = true
                          },
                        },
                        icon(EDIT_ICON),
                      ),
                    ])
                  : null,
              ]),
              m('p.muted', [
                m(m.route.Link, { href: `/u/${list.owner.username}` }, list.owner.displayName),
                `, ${list.itemCount} place${list.itemCount === 1 ? '' : 's'}, updated ${formatDate(list.updatedAt)}`,
              ]),
              list.description ? m('p', list.description) : null,
            ]),

        error ? m(ErrorNote, { text: error }) : null,

        entries.length
          ? m(
              'div.stack',
              entries.map((entry) =>
                m('article.card.place', { key: entry.id }, [
                  m('div.place-head', [
                    m(
                      m.route.Link,
                      { href: `/r/${entry.restaurant.ref}`, class: 'place-name' },
                      entry.restaurant.name,
                    ),
                    isMine
                      ? m(
                          'button.button.small.danger',
                          {
                            type: 'button',
                            disabled: busy,
                            onclick: () => removeEntry(entry.restaurant.ref),
                          },
                          'Remove',
                        )
                      : null,
                  ]),
                  m(
                    'p.muted',
                    [entry.restaurant.cuisine, entry.restaurant.city].filter(Boolean).join(', '),
                  ),
                  entry.note ? m('p.review-body', entry.note) : null,
                ]),
              ),
            )
          : m(Empty, { text: 'Nothing saved here yet.' }),
      ])
    },
  }
}
